import React from 'react';
import {
  Users, DollarSign, FileText, TrendingUp, Calendar, Filter, Download, RefreshCw, CheckCircle, XCircle, Clock, Search, Mail, Phone, Building, CreditCard, Percent, AlertTriangle, Check, X, Edit2, Save, Wallet, Send, ArrowRight, Banknote, Bell, Trash2, Eye
} from 'lucide-react';
import { useSuperAdminContext } from '../SuperAdminContext';

export default function ReportsTab() {
  const ctx = useSuperAdminContext();
  const { activeTab, exportingReport, financialReport, handleExportCSV, handleExportPDF, payouts, reportPeriod, revenueChartData, setReportPeriod, stats } = ctx;

  return (
    <>
            {activeTab === 'reports' && (
              <div className="reports-content" data-testid="reports-tab">
                <div className="section-header">
                  <h2><TrendingUp size={24} /> Financial Reports</h2>
                  <div className="header-actions">
                    <div className="period-selector">
                      <button 
                        className={`period-btn ${reportPeriod === '7d' ? 'active' : ''}`}
                        onClick={() => setReportPeriod('7d')}
                      >7 Days</button>
                      <button 
                        className={`period-btn ${reportPeriod === '30d' ? 'active' : ''}`}
                        onClick={() => setReportPeriod('30d')}
                      >30 Days</button>
                      <button 
                        className={`period-btn ${reportPeriod === '90d' ? 'active' : ''}`}
                        onClick={() => setReportPeriod('90d')}
                      >90 Days</button>
                    </div>
                    <div className="export-buttons">
                      <button 
                        className="btn-export"
                        onClick={handleExportCSV}
                        disabled={exportingReport}
                        data-testid="export-csv-btn"
                      >
                        <Download size={16} /> Export CSV
                      </button>
                      <button 
                        className="btn-export pdf"
                        onClick={handleExportPDF}
                        disabled={exportingReport}
                        data-testid="export-pdf-btn"
                      >
                        <FileText size={16} /> Export PDF
                      </button>
                    </div>
                  </div>
                </div>

                {financialReport && (
                  <>
                    {/* Summary Cards */}
                    <div className="report-summary-cards">
                      <div className="report-card volume">
                        <div className="report-card-icon"><DollarSign size={24} /></div>
                        <div className="report-card-content">
                          <span className="report-label">Total Volume</span>
                          <span className="report-value">${financialReport.summary?.total_payment_volume?.toLocaleString() || '0'}</span>
                        </div>
                      </div>
                      <div className="report-card fees">
                        <div className="report-card-icon"><Percent size={24} /></div>
                        <div className="report-card-content">
                          <span className="report-label">Fees Collected</span>
                          <span className="report-value">${financialReport.summary?.total_fees_collected?.toLocaleString() || '0'}</span>
                        </div>
                      </div>
                      <div className="report-card revenue">
                        <div className="report-card-icon"><TrendingUp size={24} /></div>
                        <div className="report-card-content">
                          <span className="report-label">Platform Revenue</span>
                          <span className="report-value">${financialReport.summary?.net_platform_revenue?.toLocaleString() || '0'}</span>
                        </div>
                      </div>
                      <div className="report-card payouts">
                        <div className="report-card-icon"><Send size={24} /></div>
                        <div className="report-card-content">
                          <span className="report-label">Payouts Sent</span>
                          <span className="report-value">${financialReport.summary?.total_payouts_sent?.toLocaleString() || '0'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Conversion Analysis */}
                    {financialReport.conversion_analysis && (
                      <div className="report-section">
                        <h3>Conversion Rate Analysis</h3>
                        <div className="conversion-stats">
                          <div className="conv-stat">
                            <span className="conv-label">Payments with Actual Conversion</span>
                            <span className="conv-value">{financialReport.conversion_analysis.payments_with_actual_conversion}</span>
                          </div>
                          <div className="conv-stat">
                            <span className="conv-label">Expected USD</span>
                            <span className="conv-value">${financialReport.conversion_analysis.total_expected_usd?.toFixed(2) || '0'}</span>
                          </div>
                          <div className="conv-stat">
                            <span className="conv-label">Actual USD Received</span>
                            <span className="conv-value">${financialReport.conversion_analysis.total_actual_usd?.toFixed(2) || '0'}</span>
                          </div>
                          <div className="conv-stat">
                            <span className="conv-label">Average Variance</span>
                            <span className={`conv-value ${financialReport.conversion_analysis.average_variance_percent >= 0 ? 'positive' : 'negative'}`}>
                              {financialReport.conversion_analysis.average_variance_percent?.toFixed(2) || '0'}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Revenue Chart */}
                    {revenueChartData.length > 0 && (
                      <div className="report-section">
                        <h3>Daily Revenue Trend</h3>
                        <div className="simple-chart">
                          {revenueChartData.slice(-14).map((day, index) => (
                            <div key={day.date} className="chart-bar-wrapper">
                              <div 
                                className="chart-bar"
                                style={{ 
                                  height: `${Math.max(5, (day.fees / Math.max(...revenueChartData.map(d => d.fees || 1))) * 100)}%`
                                }}
                                title={`${day.date}: $${day.fees.toFixed(2)} fees, ${day.count} payments`}
                              />
                              <span className="chart-label">{day.date.slice(5)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Merchants */}
                    {financialReport.top_merchants?.length > 0 && (
                      <div className="report-section">
                        <h3>Top Merchants by Volume</h3>
                        <div className="table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Rank</th>
                                <th>Business</th>
                                <th>Volume</th>
                                <th>Fees Generated</th>
                                <th>Payments</th>
                              </tr>
                            </thead>
                            <tbody>
                              {financialReport.top_merchants.map((merchant, index) => (
                                <tr key={merchant.user_id || merchant.business_name || `merchant-${index}`}>
                                  <td>#{index + 1}</td>
                                  <td><strong>{merchant.business_name}</strong></td>
                                  <td>${merchant.total_volume?.toFixed(2)}</td>
                                  <td className="text-success">${merchant.total_fees?.toFixed(2)}</td>
                                  <td>{merchant.payment_count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
    </>
  );
}
